import { useEffect, useMemo } from 'react';
import { selectShowSecclFlow, selectUserFxFee, } from 'features/invest/reducers/selectors';
import { selectShouldShowAutosave, selectUserTierMaxPotInterest, } from 'features/pots/reducers/selectors';
import { Tier } from 'features/premium/entities';
import { selectConnectionsLimit, selectRentReportingAgenciesTierMap, } from 'features/premium/selectors';
import { selectIsUserUltimate, selectUserTier, } from 'features/premium/selectors/tier';
import checkFeatureFlag from 'utils/featureFlags';
import { getMyLimits } from 'features/transfers/actions';
import { useFetchReduxed } from 'hooks/useFetchReduxed';
import { selectActiveConnectionsWithoutManual, selectIsGBUser, } from 'reducers/selectors';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { formatAmount } from 'utils/formatting';
import { getSecclAUMFees } from 'actions/invest';
import { MY_LIMITS_MODULE_NAME } from '../reducers/myLimits';
export const useMyLimitsFetch = (debug) => useFetchReduxed(MY_LIMITS_MODULE_NAME, debug);
export const useMyLimits = () => {
    const dispatch = useAppDispatch();
    const [myLimits, error, isFetching, fetch] = useMyLimitsFetch();
    const isGBUser = useAppSelector(selectIsGBUser);
    const userFxFees = useAppSelector(selectUserFxFee);
    const isUnlimited = useAppSelector(selectIsUserUltimate);
    const userInterest = useAppSelector(selectUserTierMaxPotInterest);
    const userTier = useAppSelector(selectUserTier);
    const autosaveFees = useAppSelector((store) => store.pots.savingsPlanTierFees);
    const autoInvestFees = useAppSelector((store) => store.invest.autoInvestTierFees);
    const enableSecclFlow = useAppSelector(selectShowSecclFlow);
    const tierAumFeesMap = useAppSelector((state) => state.invest.tierSecclAUMFeesMap);
    const hasAutomatedSavings = useAppSelector(selectShouldShowAutosave);
    const rentReportingAgenciesForTier = useAppSelector(selectRentReportingAgenciesTierMap);
    const connections = useAppSelector(selectActiveConnectionsWithoutManual);
    const connectionsLimitMap = useAppSelector(selectConnectionsLimit);
    const connectionsLimit = connectionsLimitMap?.[userTier];
    const featureFlags = useAppSelector((store) => store.utils.featureFlags);
    const userCreditScore = useMemo(() => {
        switch (userTier) {
            case Tier.plus:
                return checkFeatureFlag(featureFlags, 'credit_score')
                    ? { title: 'Credit score', description: 'Monthly update' }
                    : undefined;
            case Tier.pro:
                return checkFeatureFlag(featureFlags, 'credit_score_reports')
                    ? {
                        title: 'Credit score & report',
                        description: 'Monthly updates',
                    }
                    : undefined;
            case Tier.ultimate:
                return checkFeatureFlag(featureFlags, 'credit_score_alerts')
                    ? {
                        title: 'Credit score & report',
                        description: 'Real-time updates',
                    }
                    : undefined;
            default:
                return undefined;
        }
    }, [featureFlags, userTier]);
    useEffect(() => {
        if (isGBUser) {
            fetch(getMyLimits());
        }
    }, [isGBUser]);
    useEffect(() => {
        if (isGBUser && enableSecclFlow && !tierAumFeesMap) {
            dispatch(getSecclAUMFees());
        }
    }, [enableSecclFlow, isGBUser, tierAumFeesMap]);
    const limitsText = useMemo(() => {
        if (isUnlimited) {
            return 'Unlimited';
        }
        if (myLimits) {
            return `${formatAmount(myLimits?.monthly.used, myLimits?.currency)}/${formatAmount(myLimits?.monthly.limit, myLimits?.currency)}`;
        }
        return '-';
    }, [isUnlimited, myLimits]);
    const userConnectionsLimit = connectionsLimit === null
        ? 'Unlimited'
        : `${connections.length}/${connectionsLimit}`;
    if (!isGBUser) {
        return {
            myLimits,
            error,
            isUnlimited,
            isFetching,
            userConnectionsLimit,
        };
    }
    return {
        myLimits,
        error,
        isUnlimited,
        limitsText,
        isFetching,
        userFxFees: !enableSecclFlow && userFxFees !== undefined
            ? `${userFxFees}%`
            : undefined,
        userAUMFees: enableSecclFlow && tierAumFeesMap?.[userTier]
            ? `${tierAumFeesMap?.[userTier]}%`
            : undefined,
        userInterest: userInterest ? `${userInterest}% AER` : undefined,
        userAutosaveFees: 
        // eslint-disable-next-line no-nested-ternary
        hasAutomatedSavings && autosaveFees
            ? autosaveFees[userTier]
                ? formatAmount(autosaveFees[userTier], 'GBP', 2)
                : 'Free'
            : undefined,
        // eslint-disable-next-line no-nested-ternary
        autoInvestFees: autoInvestFees
            ? autoInvestFees?.[userTier]
                ? formatAmount(autoInvestFees[userTier], 'GBP', 2)
                : 'Free'
            : undefined,
        userRentReportingAgencies: rentReportingAgenciesForTier,
        userExtraMembers: isUnlimited ? 'Up to 4 people' : 'Not available',
        userConnectionsLimit,
        userCreditScore,
    };
};
